import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, Box, makeStyles, CircularProgress,
  Typography, IconButton, Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import RotateRightIcon from '@material-ui/icons/RotateRight';

import MultiLangTextField from '../../components/MultiLangTextField';
import { getAdhkarById, createAdhkar, updateAdhkar } from '../../services/tasbeehService';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    flexShrink: 0,
  },
  sectionCard: {
    padding: 24,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  arabicField: {
    '& .MuiInputBase-root': {
      direction: 'rtl',
      fontFamily: '"Traditional Arabic", "Scheherazade New", serif',
      fontSize: '1.45rem',
      lineHeight: 2.0,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

export default function TasbeehFormModal({ open, onClose, adhkarId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(adhkarId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    name: { en: '', bn: '' },
    arabic: '',
    target: 33,
  });

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (adhkarId) {
      setLoading(true);
      getAdhkarById(adhkarId)
        .then((data) => {
          if (data) {
            setForm({
              name: { en: data.name?.en || '', bn: data.name?.bn || '' },
              arabic: data.arabic || '',
              target: data.target ?? 33,
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setErrorMsg('Failed to load Dhikr details.');
          setLoading(false);
        });
    } else {
      setForm({
        name: { en: '', bn: '' },
        arabic: '',
        target: 33,
      });
      setLoading(false);
    }
  }, [open, adhkarId]);

  const handleSave = async () => {
    if (!form.name.en || !form.arabic) {
      setErrorMsg('English name and Arabic text are required.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      if (isEdit) {
        await updateAdhkar(adhkarId, form);
      } else {
        await createAdhkar(form);
      }
      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save Dhikr.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <RotateRightIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'তসবীহ / যিকির সম্পাদনা (Edit Dhikr)' : 'নতুন যিকির যোগ করুন (Add New Dhikr)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${adhkarId} • ${form.name.en || 'Dhikr'}` : 'ডিজিটাল তসবীহ কাউন্টারের জন্য যিকির সংরক্ষণ করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              যিকিরের তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            <Paper elevation={0} className={classes.sectionCard}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MultiLangTextField
                    fields={form.name}
                    onChange={(name) => setForm({ ...form, name })}
                    label="Dhikr Phrase Name"
                    title="Name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="মূল আরবি বাক্য (Arabic Text) *"
                    value={form.arabic}
                    onChange={(e) => setForm({ ...form, arabic: e.target.value })}
                    className={`${classes.field} ${classes.arabicField}`}
                    placeholder="سُبْحَانَ اللَّهِ..."
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    label="লক্ষ্যমাত্রা / কাউন্ট সংখ্যা (Target Count)"
                    value={form.target}
                    onChange={(e) => setForm({ ...form, target: parseInt(e.target.value, 10) || 33 })}
                    className={classes.field}
                    helperText="ডিফল্ট: ৩৩, ১০০ বা আপনার পছন্দমতো সংখ্যা"
                  />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'যিকির সংরক্ষণ করুন (Save Dhikr)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
